import { useMutation, useQueryClient } from '@tanstack/react-query'
import { invalidateGroup } from '@/hooks/invalidation'
import { createListHooks } from '@/hooks/lists/createListHooks'
import { useDefinedQuery, type QueryDef } from '@/hooks/useDefinedQuery'
import { withIdempotency } from '@/hooks/withIdempotency'
import { api } from '@/services/api'
import { addToBuylist, removeFromBuylist, bulkRemoveFromBuylist, getSharedBuylist } from '@/services/api/buylist'
import type { SharedBuylistResponse } from '@/types'

const KEYS = {
  list: () => ['buylist'] as const,
  quantities: () => ['buylistQuantities'] as const,
  shareToken: () => ['buylistShareToken'] as const,
  shared: (token: string) => ['sharedBuylist', token] as const,
}

const MUTATION_KEYS = {
  add: ['mutation', 'buylist', 'add'] as const,
  remove: ['mutation', 'buylist', 'remove'] as const,
}

// All buylist writes share one replay scope so queued offline changes replay
// in order. Settled keys mirror INVALIDATION_GROUPS.buylist — used by the
// runtime hooks AND the replay defaults in main.tsx (a replayed-after-reload
// mutation has only these, no hook callbacks).
const BUYLIST_SCOPE = { id: 'buylist' } as const
const BUYLIST_SETTLED_KEYS = [['buylist'], ['userQuantities']] as const

/**
 * @public Never imported by name: consumed at runtime by `lib/offlineMutations.ts`
 * (import.meta.glob over hooks/**) to register offline-replay defaults in
 * main.tsx. Un-exporting this silently breaks replay.
 */
export const buylistMutations = {
  add: {
    mutationKey: MUTATION_KEYS.add,
    mutationFn: ({
      cardId,
      quantity,
      idempotencyKey,
    }: {
      cardId: number
      quantity?: number
      idempotencyKey?: string
    }) => addToBuylist(cardId, quantity, idempotencyKey),
    offlineReplay: true,
    scope: BUYLIST_SCOPE,
    settledInvalidation: BUYLIST_SETTLED_KEYS,
  },
  remove: {
    mutationKey: MUTATION_KEYS.remove,
    mutationFn: ({ cardId, all, idempotencyKey }: { cardId: number; all?: boolean; idempotencyKey?: string }) =>
      removeFromBuylist(cardId, idempotencyKey, all || undefined),
    offlineReplay: true,
    scope: BUYLIST_SCOPE,
    settledInvalidation: BUYLIST_SETTLED_KEYS,
  },
}

// Exported for useUserQuantities.ts cache seeding
export { KEYS as BUYLIST_KEYS }

const hooks = createListHooks({
  keys: {
    list: KEYS.list,
    quantities: KEYS.quantities,
    shareToken: KEYS.shareToken,
  },
  mutationKeys: MUTATION_KEYS,
  api: {
    getList: api.getBuylist,
    addItem: api.addToBuylist,
    removeItem: api.removeFromBuylist,
    getShareToken: api.getBuylistShareToken,
    regenerateShareToken: api.regenerateBuylistShareToken,
  },
  group: 'buylist',
})

export const useBuylist = hooks.useList
export const useBuylistQuantities = hooks.useQuantities
export const useAddToBuylist = hooks.useAdd
export const useRemoveFromBuylist = hooks.useRemove
export const useBuylistShareToken = hooks.useShareToken
export const useRegenerateBuylistToken = hooks.useRegenerateToken

export function useBulkRemoveFromBuylist() {
  const queryClient = useQueryClient()
  type Vars = { cardIds: number[]; idempotencyKey?: string }
  const mutation = useMutation({
    mutationFn: ({ cardIds, idempotencyKey }: Vars) => bulkRemoveFromBuylist(cardIds, idempotencyKey),
    onSuccess: () => invalidateGroup(queryClient, 'buylist'),
  })
  type PublicVars = number[] | Vars
  const normalize = (vars: PublicVars): Vars => (Array.isArray(vars) ? { cardIds: vars } : vars)
  return withIdempotency(mutation, normalize)
}

// Shared buylist (public, no auth) — moved from useSharedLists.ts
const sharedBuylistQuery: QueryDef<SharedBuylistResponse, [string]> = {
  queryKey: KEYS.shared,
  queryFn: getSharedBuylist,
  auth: 'public',
}
export function useSharedBuylist(shareToken: string | undefined) {
  return useDefinedQuery(sharedBuylistQuery, shareToken as string, { enabled: !!shareToken })
}
